import { validateStoredValue } from "@catchy/region-config/storage-safety";
// Default GTFS details only. User-authored names remain in preferred_stop_names.
export const favouriteDetailsKey = "favourite_stop_details";
const fields = [
  "stop_id",
  "stop_name",
  "stop_type",
  "stop_code",
  "stop_lat",
  "stop_lon",
];

export function mergeFavouriteStopDetails(current, stops, favouriteIds) {
  const cache =
    current && typeof current === "object" && !Array.isArray(current)
      ? current
      : {};
  let next = cache;
  const allowedIds = Array.isArray(favouriteIds) ? new Set(favouriteIds) : null;
  if (Array.isArray(favouriteIds)) {
    const entries = Object.entries(cache).filter(([id]) => allowedIds.has(id));
    if (entries.length !== Object.keys(cache).length)
      next = Object.fromEntries(entries);
  }
  for (const stop of stops || []) {
    if (typeof stop?.stop_id !== "string" || !stop.stop_name) continue;
    if (allowedIds && !allowedIds.has(stop.stop_id)) continue;
    const details = Object.fromEntries(
      fields.map((field) => [field, stop[field] ?? null]),
    );
    const checked = validateStoredValue(
      favouriteDetailsKey,
      Object.fromEntries([[stop.stop_id, details]]),
    );
    if (!checked.ok || !Object.hasOwn(checked.value, stop.stop_id)) continue;
    if (fields.every((field) => next[stop.stop_id]?.[field] === details[field]))
      continue;
    if (next === cache) next = { ...cache };
    next[stop.stop_id] = details;
  }
  // Absent IDs never remove either cached details or favourites.
  return next;
}

export function updateFavouriteStopDetails(current, stopId, stop, added) {
  if (added)
    return mergeFavouriteStopDetails(
      current,
      stop ? [{ ...stop, stop_id: stopId }] : [],
    );
  if (!current || !Object.hasOwn(current, stopId)) return current;
  const next = { ...current };
  delete next[stopId];
  return next;
}

export function parseFavouriteStopIds(value) {
  if (typeof value !== "string") throw new Error("stop_ids must be JSON");
  const ids = JSON.parse(value);
  if (
    !Array.isArray(ids) ||
    ids.length > 100 ||
    ids.some((id) => typeof id !== "string" || !id || id.length > 200)
  ) {
    throw new Error("stop_ids must be an array of at most 100 stop IDs");
  }
  return [...new Set(ids)];
}

export function selectExactFavouriteStops(stops, ids) {
  const byId = new Map(stops.map((stop) => [stop.stop_id, stop]));
  // Auckland public parents retain their child IDs. Preserve the requested ID
  // in the response, so neither favourite identity nor custom names migrate.
  for (const stop of stops) {
    for (const id of stop.source_stop_ids || [])
      if (!byId.has(id)) byId.set(id, stop);
  }
  return ids.flatMap((id) =>
    byId.has(id) ? [{ ...byId.get(id), stop_id: id }] : [],
  );
}

export function buildFavouriteDetailUrls(ids) {
  const urls = [];
  let batch = [];
  const urlFor = (values) =>
    `/api/stops/search?stop_ids=${encodeURIComponent(JSON.stringify(values))}`;
  for (const id of ids) {
    if (
      batch.length &&
      (batch.length === 100 || urlFor([...batch, id]).length > 6000)
    ) {
      urls.push(urlFor(batch));
      batch = [];
    }
    batch.push(id);
  }
  if (batch.length) urls.push(urlFor(batch));
  return urls;
}
